import React, { FunctionComponent, ReactNode, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NativeSyntheticEvent, TextLayoutEventData, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import styled, { useTheme } from 'styled-components/native'

import { getChatHeaderSubTitleColor, getChatHeaderSubTitleIconColor } from './chatHeaderSubTitleStyles'
import { ChatMenu } from './ChatMenu'
import { ChatMenuItem } from './ChatMenuItem'
import { BodyText, Size } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { TextBadge } from '../../atoms/icons/TextBadge'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { Tooltip, TOOLTIP_TRIGGER_ICON, TooltipMaxWidth } from '../../atoms/tooltip/Tooltip'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ChatHeaderProps = {
  titleMsg: string
  subTitleMsg?: string | React.ReactNode
  subTitleTooltipMsg?: string
  textBadgeMsg?: string
  onClosePress: () => void
  closeStyle?: CloseStyles
  titleCentered?: boolean
  showClose?: boolean
  showChevron?: boolean
  showMenu?: boolean
  markMessagesAsUnread?: () => void
  onChevronPressed?: () => void
  showDivider?: boolean
  TitleBadge?: ReactNode
  isScrolled?: boolean
}

export enum CloseStyles {
  BackArrow = 'BackArrow',
  XIcon = 'XIcon',
}

const ChatHeaderContainer = styled(SafeAreaView)<{ theme: ThemeType; isScrolled?: boolean }>(
  ({ theme, isScrolled }) => ({
    padding: theme.spacing['0px'],
    // Use flat background color for Aurora when not scrolled, 60% opacity with blur when scrolled, transparent for non-Aurora
    backgroundColor: theme.isAuroraTheme
      ? isScrolled
        ? theme.colors.components.chatHeader.backgroundScrolled.background
        : theme.colors.components.chatHeader.background.background
      : 'transparent',
    // backdropFilter only works on web, not native mobile
    ...(IS_WEB && theme.isAuroraTheme && isScrolled && { backdropFilter: 'blur(8px)' }),
  }),
)

const HeaderContainer = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing['0px'],
  padding: `10px ${theme.spacing['0px']}`,
  alignItems: 'center',
}))

const HeaderTextContainer = styled.View<{ centered?: boolean; theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['24px'],
  flexShrink: 1,
  flexGrow: 1,
  justifyContent: 'center',
}))

const TitleContainer = styled.View({
  flexDirection: 'row',
})

const SubTitleContainer = styled.View({
  ...(IS_WEB ? { display: 'inline' } : { position: 'relative' }),
})

const SubTitleTooltipContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(IS_WEB
    ? {
        display: 'inline-flex',
        marginLeft: theme.spacing['4px'],
        verticalAlign: 'middle',
      }
    : { position: 'absolute' }),
}))

const CHAT_HEADER_SUBTITLE_TOOLTIP_TEST_ID = 'ChatHeader-subTitle'
const CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE = 16
const CHAT_HEADER_SUBTITLE_TOOLTIP_GAP = 4
const CHAT_HEADER_SUBTITLE_TOOLTIP_HIT_SLOP = { top: 24, right: 24, bottom: 24, left: 0 }

// Native Text lays out nested Views as inline attachments. This reserves the icon slot in the text flow while the
// accessible Tooltip remains an absolutely positioned sibling.
const SubTitleTooltipInlineAnchor = styled.View({
  width: CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE + CHAT_HEADER_SUBTITLE_TOOLTIP_GAP,
  height: CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE,
})

const ArrowIconContainer = styled(PressableOpacity)({
  marginTop: '2px',
  marginLeft: '20px',
})

const XIconContainer = styled(PressableOpacity)({
  justifyContent: 'center',
  marginLeft: '20px',
})

const RightContainer = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  paddingRight: theme.spacing['16px'],
  position: 'relative',
}))

const ChatMenuContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingRight: theme.spacing['8px'],
}))

/**
 * This component is the header for the chat screen
 * which consists of the receiver's name, back arrow, timestamp, and badge
 */
export const ChatHeader: FunctionComponent<ChatHeaderProps> = ({
  titleMsg,
  textBadgeMsg,
  subTitleMsg,
  subTitleTooltipMsg,
  onClosePress,
  closeStyle = CloseStyles.BackArrow,
  titleCentered,
  showClose = true,
  showChevron = false,
  showMenu,
  markMessagesAsUnread,
  onChevronPressed,
  showDivider = true,
  TitleBadge,
  isScrolled = false,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme() as ThemeType
  const { colors, isAuroraTheme } = theme
  const subTitleColor = getChatHeaderSubTitleColor(theme)
  const subTitleIconColor = getChatHeaderSubTitleIconColor(theme)
  const [subTitleTooltipPosition, setSubTitleTooltipPosition] = useState<{ left: number; top: number }>()

  const positionSubTitleTooltip = useCallback((event: NativeSyntheticEvent<TextLayoutEventData>) => {
    const lastLine = event.nativeEvent.lines.at(-1)
    if (!lastLine) return

    const nextPosition = {
      left: lastLine.x + lastLine.width - CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE,
      top: lastLine.y + Math.max(0, (lastLine.height - CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE) / 2),
    }
    setSubTitleTooltipPosition((currentPosition) =>
      currentPosition?.left === nextPosition.left && currentPosition.top === nextPosition.top
        ? currentPosition
        : nextPosition,
    )
  }, [])

  return (
    <ChatHeaderContainer testID={tID('ChatHeader')} edges={['top']} isScrolled={isScrolled}>
      <HeaderContainer edges={['left', 'right']}>
        {closeStyle === CloseStyles.BackArrow && showClose && (
          <ArrowIconContainer
            testID={tID('ChatHeader-back')}
            onPress={onClosePress}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Back button',
              description: 'Accessibility label for chat header back button',
            })}
          >
            <ArrowIcon
              size={24}
              fillColor={colors.iconDefault}
              direction={ArrowIconDirection.LEFT}
              testID={tID('ChatHeader-backArrowIcon')}
            />
          </ArrowIconContainer>
        )}
        <HeaderTextContainer centered={titleCentered} testID={tID('ChatHeader-headerTextContainer')}>
          <TitleContainer>
            <Title size={TitleSize.SMALL} text={titleMsg} />
            {TitleBadge}
          </TitleContainer>
          {!!subTitleMsg && !subTitleTooltipMsg && (
            <BodyText
              text={subTitleMsg}
              testID={tID('ChatHeader-subTitle')}
              size={Size.SMALL}
              color={subTitleColor}
              accessibilityRole={AccessibilityRolesNative.TEXT}
            />
          )}
          {!!subTitleMsg && !!subTitleTooltipMsg && (
            <SubTitleContainer>
              <BodyText
                text={subTitleMsg}
                testID={tID('ChatHeader-subTitle')}
                size={Size.SMALL}
                color={subTitleColor}
                accessibilityRole={AccessibilityRolesNative.TEXT}
                onTextLayout={IS_WEB ? undefined : positionSubTitleTooltip}
              >
                {!IS_WEB && (
                  <SubTitleTooltipInlineAnchor
                    accessible={false}
                    accessibilityElementsHidden
                    collapsable={false}
                    importantForAccessibility='no-hide-descendants'
                    pointerEvents='none'
                  />
                )}
              </BodyText>
              {(IS_WEB || subTitleTooltipPosition) && (
                <SubTitleTooltipContainer style={IS_WEB ? undefined : subTitleTooltipPosition}>
                  <Tooltip
                    content={subTitleTooltipMsg}
                    testID={CHAT_HEADER_SUBTITLE_TOOLTIP_TEST_ID}
                    placement='bottom right'
                    hoverEnabled={IS_WEB}
                    openOnPress
                    closeOnScroll
                    tooltipSize={TooltipMaxWidth.WIDE}
                    hitSlop={CHAT_HEADER_SUBTITLE_TOOLTIP_HIT_SLOP}
                    triggerConfig={{
                      icon: TOOLTIP_TRIGGER_ICON.INFO,
                      size: CHAT_HEADER_SUBTITLE_TOOLTIP_ICON_SIZE,
                      fillColor: subTitleIconColor,
                    }}
                    wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'More information about the Lyra AI disclaimer',
                      description: 'Accessibility label for the Lyra AI chat header disclaimer tooltip',
                    })}
                  />
                </SubTitleTooltipContainer>
              )}
            </SubTitleContainer>
          )}
        </HeaderTextContainer>
        <RightContainer edges={['right']}>
          {showMenu && (
            <ChatMenuContainer>
              <ChatMenu>
                <ChatMenuItem
                  formattedText={<FormattedMessage defaultMessage='Mark unread' description='Mark unread' />}
                  name='markUnread'
                  onPress={() => {
                    markMessagesAsUnread && markMessagesAsUnread()
                  }}
                  key='markUnread'
                />
              </ChatMenu>
            </ChatMenuContainer>
          )}
          {Boolean(textBadgeMsg) && (
            <TextBadge text={textBadgeMsg ?? ''} variant={BadgeVariant.ALERT} shape={BadgeShape.RECT} />
          )}
          {showClose && closeStyle === CloseStyles.XIcon && (
            <XIconContainer testID={tID('ChatHeader-back')} onPress={onClosePress}>
              <XIcon fillColor={colors.iconDefault} />
            </XIconContainer>
          )}
          {showChevron && (
            <XIconContainer
              testID={tID('ChatHeader-close')}
              onPress={onChevronPressed ?? onClosePress}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close messenger',
                description: 'Accessibility label for close messenger button',
              })}
            >
              <ChevronIcon
                fillColor={colors.iconDefault}
                direction={ChevronIconDirection.UP}
                testID={tID('ChatHeader-chevronIcon')}
              />
            </XIconContainer>
          )}
        </RightContainer>
      </HeaderContainer>
      {showDivider && (
        <View>
          <Divider color={isAuroraTheme ? colors.borderDefault : undefined} />
        </View>
      )}
    </ChatHeaderContainer>
  )
}
